import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import PasswordInput from '../components/PasswordInput';
import { passwordPolicyError } from '../lib/password';
import { useAuth } from '../context/AuthContext';
import { setToken } from '../lib/authToken';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResetPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);
  const [role, setRole] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    const pwdError = passwordPolicyError(password);
    if (pwdError) { setError(t(pwdError)); return; }
    if (password !== confirm) { setError(t('auth.errorPasswordsMatch')); return; }
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password }),
      });
      const data = await res.json();
      if (res.ok) {
        if (data.token) setToken(data.token);
        if (data.user) setRole(data.user.role);
        await refreshUser();
        setDone(true);
      } else {
        setError(data.detail || t('auth.resetError2'));
      }
    } catch {
      setError(t('auth.networkError'));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="container mx-auto px-4 max-w-md py-16 text-center">
          <p className="text-gray-500">{t('auth.invalidLink')}</p>
          <button onClick={() => navigate('/forgot-password')} className="mt-4 text-[#134168] font-medium hover:underline">{t('auth.requestNewLink')}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="container mx-auto px-4 max-w-md py-16">
        <div className="bg-white rounded-3xl p-8 shadow-sm">
          {done ? (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 text-3xl">✅</div>
              <h1 className="text-2xl font-bold text-[#134168] mb-3">{t('auth.passwordUpdated')}</h1>
              <p className="text-gray-600 text-sm mb-6">{t('auth.passwordResetOk')}</p>
              <button
                onClick={() => {
                  if (role === 'provider') navigate('/provider/dashboard');
                  else if (role === 'admin') navigate('/admin/dashboard');
                  else navigate('/client/dashboard');
                  window.location.reload();
                }}
                className="w-full bg-[#134168] text-white py-3 rounded-full font-bold hover:bg-[#39b4aa] transition-colors"
              >
                {t('auth.goToDashboard', 'Ir a mi Panel')}
              </button>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-bold text-[#134168] mb-2">{t('auth.newPasswordTitle')}</h1>
              <p className="text-gray-500 text-sm mb-6">{t('auth.newPasswordIntro')}</p>

              {error && <div className="mb-4 text-red-600 text-sm bg-red-50 border border-red-200 rounded-xl p-3">{error}</div>}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth.newPasswordTitle')}</label>
                  <PasswordInput required value={password} onChange={e => setPassword(e.target.value)} placeholder={t('auth.min8Chars')} className="px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#134168]" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth.confirmPassword')}</label>
                  <PasswordInput required value={confirm} onChange={e => setConfirm(e.target.value)} placeholder={t('auth.repeatPassword')} className="px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#134168]" />
                </div>
                <button type="submit" disabled={loading} className="w-full bg-[#134168] text-white py-3 rounded-full font-bold hover:bg-[#39b4aa] transition-colors disabled:opacity-50">
                  {loading ? t('auth.savingState') : t('auth.setNewPassword')}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
